import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Globe } from 'lucide-react';
import './RSVP.css';
import { LanguageContext } from '../LanguageContext';
import { GAPP_SCRIPT_API_KEY, GAPP_SCRIPT_URL } from '../constants/constants';
import Footer from './Footer';

// Type definitions
type AttendeeType = 'primary' | 'partner' | 'child';
type Attendee = {
  name: string;
  type: AttendeeType;
  dietaryRequirements: string[];
  otherDietaryRequirements: string;
};

type FormData = {
  attendance: string;
  fullName: string;
  partnerName: string;
  numberOfKids: number;
  transportRequired: string;
  attendees: Attendee[];
};

// Language translations
const translations = {
  en: {
    title: "RSVP",
    subtitle: "We hope you can join us on our special day!",
    attendanceQuestion: "Can you attend our wedding?",
    attendanceYes: "Yes, I'll be there to celebrate!",
    attendanceNo: "Unfortunately, I can't make it",
    fullName: "Your Full Name",
    fullNamePlaceholder: "Enter your full name",
    partnerName: "Your Partner's Full Name (if applicable)",
    partnerNamePlaceholder: "Enter your partner's name",
    numberOfKids: "Number of Children Attending",
    childrenInfo: "Children Information",
    childName: "Child",
    childNamePlaceholder: "Child's name",
    menuFor: "Menu for",
    vegetarian: "Vegetarian",
    vegan: "Vegan",
    glutenFree: "Gluten-Free",
    dairyFree: "Dairy-Free",
    otherAllergies: "Other allergies or intolerances",
    otherAllergiesPlaceholder: "Please specify any other dietary requirements",
    transportation: "Would you like to use our transportation? (Bucharest to Crevedia and back)",
    transportationYes: "Yes, I'd like to use the provided transportation",
    transportationNo: "No, I'll arrange my own transportation",
    submitting: "Submitting...",
    submit: "Submit RSVP",
    successMessageAttending: "✓ Thank you for your RSVP! We look forward to celebrating with you.",
    successMessageNotAttending: "✓ Thank you for your RSVP! If you change your mind, don't hesitate to contact us.",
    errorMessage: "✗ Something went wrong. Please try again or contact us directly.",
    networkError: "⚠ Error submitting RSVP. Check your internet connection or try again later.",
    deadline: "Please confirm by April 15, 2025"
  },
  ro: {
    title: "Confirmare",
    subtitle: "Sperăm că vei putea fi alături de noi în ziua noastră specială!",
    attendanceQuestion: "Poți participa la nunta noastră?",
    attendanceYes: "Da, voi fi acolo să sărbătoresc!",
    attendanceNo: "Din păcate, nu pot participa",
    fullName: "Numele tău complet",
    fullNamePlaceholder: "Introdu numele complet",
    partnerName: "Numele complet al partenerului (dacă este cazul)",
    partnerNamePlaceholder: "Introdu numele partenerului",
    numberOfKids: "Numărul de copii care vă însoțesc",
    childrenInfo: "Informații despre copii",
    childName: "Copil",
    childNamePlaceholder: "Numele copilului",
    menuFor: "Meniu pentru",
    vegetarian: "Vegetarian",
    vegan: "Vegan",
    glutenFree: "Fără gluten",
    dairyFree: "Fără lactate",
    otherAllergies: "Alte alergii sau intoleranțe",
    otherAllergiesPlaceholder: "Te rugăm să specifici orice alte cerințe alimentare",
    transportation: "Dorești să folosești transportul nostru? (București - Crevedia și retur)",
    transportationYes: "Da, aș dori să folosesc transportul asigurat",
    transportationNo: "Nu, voi organiza propriul transport",
    submitting: "Se trimite...",
    submit: "Trimite confirmarea",
    successMessageAttending: "✓ Îți mulțumim pentru confirmare! Așteptăm cu nerăbdare să sărbătorim împreună.",
    successMessageNotAttending: "✓ Îți mulțumim pentru confirmare! Dacă te răzgândești, nu ezita să ne contactezi!",
    errorMessage: "✗ Ceva nu a funcționat. Te rugăm să încerci din nou sau să ne contactezi direct.",
    networkError: "⚠ Eroare la trimiterea confirmării. Verifică conexiunea la internet sau încearcă mai târziu.",
    deadline: "Te rugăm să confirmi până pe 15 aprilie 2025"
  }
};

const RSVP: React.FC = () => {
    const { language, setLanguage } = useContext(LanguageContext);
    const [formData, setFormData] = useState<FormData>({
        attendance: 'Yes',
        fullName: '',
        partnerName: '',
        numberOfKids: 0,
        transportRequired: 'No',
        attendees: [] as Attendee[]
    });
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Get translations based on selected language
    const t = translations[language];

    // Initialize primary attendee when form loads
    useEffect(() => {
        if (formData.attendees.length === 0) {
            setFormData(prev => ({
                ...prev,
                attendees: [{
                    name: prev.fullName,
                    type: 'primary',
                    dietaryRequirements: [],
                    otherDietaryRequirements: ''
                }]
            }));
        }
    }, []);

    // Update attendees list when inputs change
    useEffect(() => {
        const updatedAttendees = [...formData.attendees];
        
        // Ensure primary attendee exists
        if (updatedAttendees.length === 0 || !updatedAttendees.some(a => a.type === 'primary')) {
            updatedAttendees.push({
                name: formData.fullName,
                type: 'primary',
                dietaryRequirements: [],
                otherDietaryRequirements: ''
            });
        } else {
            // Update primary attendee name
            const primaryIndex = updatedAttendees.findIndex(a => a.type === 'primary');
            if (primaryIndex >= 0) {
                updatedAttendees[primaryIndex] = {
                    ...updatedAttendees[primaryIndex],
                    name: formData.fullName
                };
            }
        }

        // Handle partner
        if (formData.partnerName) {
            // Check if partner already exists
            const partnerIndex = updatedAttendees.findIndex(a => a.type === 'partner');
            if (partnerIndex >= 0) {
                // Update existing partner
                updatedAttendees[partnerIndex] = {
                    ...updatedAttendees[partnerIndex],
                    name: formData.partnerName
                };
            } else {
                // Add new partner
                updatedAttendees.push({
                    name: formData.partnerName,
                    type: 'partner',
                    dietaryRequirements: [],
                    otherDietaryRequirements: ''
                });
            }
        } else {
            // Remove partner if name is empty
            const partnerIndex = updatedAttendees.findIndex(a => a.type === 'partner');
            if (partnerIndex >= 0) {
                updatedAttendees.splice(partnerIndex, 1);
            }
        }

        // Handle kids
        const currentKids = updatedAttendees.filter(a => a.type === 'child');
        const kidsCount = parseInt(formData.numberOfKids.toString()) || 0;

        if (currentKids.length < kidsCount) {
            // Add more kids
            for (let i = currentKids.length; i < kidsCount; i++) {
                updatedAttendees.push({
                    name: `${t.childName} ${i + 1}`,
                    type: 'child',
                    dietaryRequirements: [],
                    otherDietaryRequirements: ''
                });
            }
        } else if (currentKids.length > kidsCount) {
            // Remove excess kids
            const toRemove = currentKids.length - kidsCount;
            for (let i = 0; i < toRemove; i++) {
                const index = updatedAttendees.findIndex(a => a.type === 'child');
                if (index >= 0) {
                    updatedAttendees.splice(index, 1);
                }
            }
        }

        setFormData(prev => ({
            ...prev,
            attendees: updatedAttendees
        }));
    }, [formData.fullName, formData.partnerName, formData.numberOfKids, t.childName]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleAttendeeChange = (index: number, field: string, value: any) => {
        const updatedAttendees = [...formData.attendees];
        updatedAttendees[index] = {
            ...updatedAttendees[index],
            [field]: value
        };
        setFormData({
            ...formData,
            attendees: updatedAttendees
        });
    };

    const handleAttendeeDietaryChange = (index: number, value: string, checked: boolean) => {
        const updatedAttendees = [...formData.attendees];
        const currentRequirements = [...updatedAttendees[index].dietaryRequirements];
        
        if (checked) {
            updatedAttendees[index] = {
                ...updatedAttendees[index],
                dietaryRequirements: [...currentRequirements, value]
            };
        } else {
            updatedAttendees[index] = {
                ...updatedAttendees[index],
                dietaryRequirements: currentRequirements.filter(item => item !== value)
            };
        }
        
        setFormData({
            ...formData,
            attendees: updatedAttendees
        });
    };

    const updateKidName = (index: number, name: string) => {
        // Get all children
        const children = formData.attendees.filter(a => a.type === 'child');
        
        if (index < children.length) {
            // Find the actual index of this child in the main attendees array
            const childToUpdate = children[index];
            const actualIndex = formData.attendees.findIndex(a => a === childToUpdate);
            
            if (actualIndex >= 0) {
                const updatedAttendees = [...formData.attendees];
                updatedAttendees[actualIndex] = {
                    ...updatedAttendees[actualIndex],
                    name
                };
                setFormData({
                    ...formData,
                    attendees: updatedAttendees
                });
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setMessage('');

        try {
            // Process attendee data for submission
            const attendeesData = formData.attendees.map(attendee => ({
                name: attendee.name,
                type: attendee.type,
                dietary: attendee.otherDietaryRequirements.length > 0 ?
                [...attendee.dietaryRequirements, attendee.otherDietaryRequirements]:
                [...attendee.dietaryRequirements]
            }));

            // Prepare data for submission
            const submissionData = {
                attendance: formData.attendance,
                fullName: formData.fullName,
                partnerName: formData.partnerName,
                numberOfKids: formData.numberOfKids,
                transportRequired: formData.transportRequired,
                attendeesData: attendeesData,
                method: "saveRSVP",
                apiKey: GAPP_SCRIPT_API_KEY
            };

            const response = await fetch(GAPP_SCRIPT_URL, {
                method: 'POST',
                redirect: "follow",
                headers: { 'Content-Type': 'text/plain;charset=utf-8' },
                body: JSON.stringify(submissionData),
            });

            const result = await response.json();
            if (result.status === 'success') {
                if(submissionData.attendance === 'Yes') {
                    setMessage(t.successMessageAttending);
                } else {
                    setMessage(t.successMessageNotAttending);
                }
                setFormData({
                    attendance: 'Yes',
                    fullName: '',
                    partnerName: '',
                    numberOfKids: 0,
                    transportRequired: 'No',
                    attendees: [{
                        name: '',
                        type: 'primary',
                        dietaryRequirements: [],
                        otherDietaryRequirements: ''
                    }]
                });
            } else {
                setMessage(t.errorMessage);
            }
        } catch (error) {
            console.error(error);
            setMessage(t.networkError);
        } finally {
            setSubmitting(false);
        }
    };

    // Helper function to render dietary options for an attendee
    const renderDietaryOptions = (attendee: Attendee, index: number) => {
        const dietaryOptions = [
            { id: 'vegetarian', value: 'Vegetarian', label: t.vegetarian },
            { id: 'vegan', value: 'Vegan', label: t.vegan },
            { id: 'gluten-free', value: 'Gluten-Free', label: t.glutenFree },
            { id: 'dairy-free', value: 'Dairy-Free', label: t.dairyFree }
        ];

        return (
            <div className="dietary-options">
                <h4 className="dietary-title">
                    {t.menuFor} {attendee.name || attendee.type}
                </h4>
                <div className="dietary-checkboxes">
                    {dietaryOptions.map(option => (
                        <div key={`${option.id}-${index}`} className="dietary-checkbox">
                            <input
                                type="checkbox"
                                id={`${option.id}-${index}`}
                                checked={attendee.dietaryRequirements.includes(option.value)}
                                onChange={(e) => handleAttendeeDietaryChange(index, option.value, e.target.checked)}
                                className="checkbox"
                            />
                            <label htmlFor={`${option.id}-${index}`} className="checkbox-label">
                                {option.label}
                            </label>
                        </div>
                    ))}
                    
                    <div className="other-dietary">
                        <label htmlFor={`other-${index}`} className="other-dietary-label">
                            {t.otherAllergies}
                        </label>
                        <textarea
                            id={`other-${index}`}
                            value={attendee.otherDietaryRequirements}
                            onChange={(e) => handleAttendeeChange(index, 'otherDietaryRequirements', e.target.value)}
                            placeholder={t.otherAllergiesPlaceholder}
                            className="other-dietary-input"
                            rows={2}
                        />
                    </div>
                </div>
            </div>
        );
    };

    // Render children name inputs
    const renderChildrenInputs = () => {
        const children = formData.attendees.filter(a => a.type === 'child');
        
        return children.map((child, index) => {
            // Find the actual index of this child in the attendees array
            const childIndex = formData.attendees.findIndex(a => a === child);
            
            return (
                <div key={`child-${childIndex}`} className="child-section">
                    <div className="child-name-container">
                        <label className="child-label">
                            {t.childName} {index + 1}
                        </label>
                        <input
                            type="text"
                            value={child.name === `${t.childName} ${index + 1}` ? '' : child.name}
                            onChange={(e) => updateKidName(index, e.target.value)}
                            placeholder={t.childNamePlaceholder}
                            className="child-input"
                        />
                    </div>
                    {renderDietaryOptions(child, childIndex)}
                </div>
            );
        });
    };

    return (
        <div className="rsvp-container">
            <div className="rsvp-content">
                <div className="decorative-element top-left">
                    <img src="/images/flowers.svg" alt="" className="decoration-image" />
                </div>
                <div className="decorative-element top-right">
                    <img src="/images/disco-ball.svg" alt="" className="decoration-image" />
                </div>
                
                <h2 className="rsvp-title">{t.title}</h2>
                <p className="rsvp-subtitle">{t.subtitle}</p>
                <p className="rsvp-deadline">{t.deadline}</p>
                
                <form onSubmit={handleSubmit} className="rsvp-form">
                    {/* Attendance */}
                    <div className="form-group">
                        <label className="form-label">
                            {t.attendanceQuestion}
                        </label>
                        <div className="radio-group">
                            <label className={`radio-button ${formData.attendance === 'Yes' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="attendance"
                                    value="Yes"
                                    checked={formData.attendance === 'Yes'}
                                    onChange={handleChange}
                                />
                                <span className="radio-text">{t.attendanceYes}</span>
                            </label>
                            <label className={`radio-button ${formData.attendance === 'No' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="attendance"
                                    value="No"
                                    checked={formData.attendance === 'No'}
                                    onChange={handleChange}
                                />
                                <span className="radio-text">{t.attendanceNo}</span>
                            </label>
                        </div>
                    </div>

                    {/* Full Name - always shown */}
                    <div className="form-group">
                        <label className="form-label">
                            {t.fullName}
                        </label>
                        <input
                            type="text"
                            name="fullName"
                            placeholder={t.fullNamePlaceholder}
                            value={formData.fullName}
                            onChange={handleChange}
                            required
                            className="form-input"
                        />
                    </div>

                    {formData.attendance === 'Yes' && (
                        <>
                            {/* Your Menu Requirements */}
                            {formData.fullName && formData.attendees.some(a => a.type === 'primary') && 
                                renderDietaryOptions(
                                    formData.attendees.find(a => a.type === 'primary')!, 
                                    formData.attendees.findIndex(a => a.type === 'primary')
                                )
                            }

                            {/* Partner Name */}
                            <div className="form-group">
                                <label className="form-label">
                                    {t.partnerName}
                                </label>
                                <input
                                    type="text"
                                    name="partnerName"
                                    placeholder={t.partnerNamePlaceholder}
                                    value={formData.partnerName}
                                    onChange={handleChange}
                                    className="form-input"
                                />
                            </div>

                            {/* Partner's Menu Requirements */}
                            {formData.partnerName && formData.attendees.findIndex(a => a.type === 'partner') >= 0 && 
                                renderDietaryOptions(
                                    formData.attendees.find(a => a.type === 'partner')!, 
                                    formData.attendees.findIndex(a => a.type === 'partner')
                                )
                            }

                            {/* Number of Kids */}
                            <div className="form-group">
                                <label className="form-label">
                                    {t.numberOfKids}
                                </label>
                                <input
                                    type="number"
                                    name="numberOfKids"
                                    min="0"
                                    max="10"
                                    value={formData.numberOfKids}
                                    onChange={handleChange}
                                    className="form-input number-input"
                                />
                            </div>

                            {/* Children Names and Menu Requirements */}
                            {parseInt(formData.numberOfKids.toString()) > 0 && (
                                <div className="children-section">
                                    <h3 className="section-title">{t.childrenInfo}</h3>
                                    {renderChildrenInputs()}
                                </div>
                            )}

                            {/* Transportation */}
                            <div className="form-group">
                                <label className="form-label">
                                    {t.transportation}
                                </label>
                                <div className="radio-group">
                                    <label className={`radio-button ${formData.transportRequired === 'Yes' ? 'selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="transportRequired"
                                            value="Yes"
                                            checked={formData.transportRequired === 'Yes'}
                                            onChange={handleChange}
                                        />
                                        <span className="radio-text">{t.transportationYes}</span>
                                    </label>
                                    <label className={`radio-button ${formData.transportRequired === 'No' ? 'selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="transportRequired"
                                            value="No"
                                            checked={formData.transportRequired === 'No'}
                                            onChange={handleChange}
                                        />
                                        <span className="radio-text">{t.transportationNo}</span>
                                    </label>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={submitting}
                        className="submit-button"
                    >
                        {submitting ? t.submitting : t.submit}
                    </button>
                </form>
                
                {message && (
                    <div className={`message ${message.includes('✓') ? 'success' : 'error'}`}>
                        <p>{message}</p>
                    </div>
                )}

                <Footer/>
            </div>
        </div>
    );
};

export default RSVP;